// client/src/features/ledger/search.ts — SCR-LEDGER-001's URL state, and the window it names ·
// „Bilanț" (04 §9.2.3).
//
// §9.2.3 is NORMATIVE: a filtered view lives in the URL so it is shareable, survives a refresh
// and Back is a filter-undo. Everything a person can set on this page is a search param — the
// window, the apartment, the tab, the three kind toggles, the category, the method, the search
// text, the grouping and the matrix measure — because every one of them changes what a shared
// link shows. The one thing kept local is which category groups are collapsed, which is a
// reading position and not a view (D-2149).
//
// THE TRAPS THE OTHER SCHEMAS NAME APPLY HERE TOO. TanStack Router parses search values with
// `JSON.parse`, so `?year=2025` arrives as a NUMBER and `?month=03` as a STRING: `z.coerce`
// takes both (D-1197). A boolean is `true` when typed and `'true'` when a control wrote it, so
// the toggles take `zUrlBoolean` (`features/rent/search.ts`'s `late`, D-732). And NO ZOD
// `.default()` on the year: a default evaluated at parse time bakes „the year the link was
// opened" into every URL the router normalises. The window is resolved once, explicitly, by
// `resolveLedgerFilters`.

import type { TFunction } from 'i18next';
import { z } from 'zod';

import type { CashFlowKind, EconomicPnlPeriod, PaymentMethod, ReportingPeriodKind } from '@/client';
import { formatPeriod, shiftPeriod, type Period } from '@/features/rent/periods';
import { MAX_REPORT_YEAR, MIN_REPORT_YEAR, quarterOf } from '@/features/reports/search';
import { formatDate } from '@/lib/format';
import { urlBoolean, zUrlBoolean } from '@/lib/url-state';

import { isPaymentMethod } from './enums';

/** The four window kinds the API takes — `ReportingPeriod` has no custom range, so no „Interval". */
export const PERIOD_KINDS = [
  'MONTH',
  'QUARTER',
  'YEAR',
  'YTD',
] as const satisfies readonly ReportingPeriodKind[];

/**
 * The four tabs, in the order the page shows them. „Rapoarte" is the reports' export, moved here
 * when the gallery left the sidebar (D-2810); it is drawn only for a `Reports.Read` holder.
 */
export const LEDGER_TABS = ['registru', 'matrice', 'inafara', 'rapoarte'] as const;

export type LedgerTab = (typeof LEDGER_TABS)[number];

/** What a matrix cell shows. */
export const LEDGER_MEASURES = ['in', 'out', 'net'] as const;

export type LedgerMeasure = (typeof LEDGER_MEASURES)[number];

export const ledgerSearchSchema = z.object({
  period: z.enum(PERIOD_KINDS).optional(),
  year: z.coerce.number().int().min(MIN_REPORT_YEAR).max(MAX_REPORT_YEAR).optional(),
  month: z.coerce.number().int().min(1).max(12).optional(),
  quarter: z.coerce.number().int().min(1).max(4).optional(),
  propertyId: z.string().uuid().optional(),
  tab: z.enum(LEDGER_TABS).optional(),
  q: z.string().optional(),
  category: z.string().optional(),
  // A method the contract does not name is dropped rather than sent: the API has no method
  // filter, and a stale value would silently hide every row.
  method: z.custom<PaymentMethod>(isPaymentMethod).optional(),
  rent: zUrlBoolean.optional(),
  expenses: zUrlBoolean.optional(),
  memo: zUrlBoolean.optional(),
  groupBy: z.enum(['category']).optional(),
  measure: z.enum(LEDGER_MEASURES).optional(),
});

export type LedgerSearch = z.infer<typeof ledgerSearchSchema>;

/** Every setting of the page, resolved — what the query is sent and what the rows are filtered by. */
export interface LedgerFilters {
  period: ReportingPeriodKind;
  year: number;
  month: number | undefined;
  quarter: number | undefined;
  propertyId: string | undefined;
  tab: LedgerTab;
  /** Trimmed; empty when there is none. */
  q: string;
  category: string | undefined;
  method: PaymentMethod | undefined;
  /** The three kind toggles: on unless the URL says `false`. */
  rent: boolean;
  expenses: boolean;
  memo: boolean;
  groupByCategory: boolean;
  measure: LedgerMeasure;
}

/**
 * The URL's settings, resolved against „now" — the year, the month and the quarter default to
 * the current Romanian ones, which is also the server's own default, so an unfiltered screen and
 * an unfiltered API call show the same window.
 */
export function resolveLedgerFilters(search: LedgerSearch, now: Date): LedgerFilters {
  const period = search.period ?? 'YTD';

  return {
    period,
    year: search.year ?? now.getFullYear(),
    month: period === 'MONTH' ? (search.month ?? now.getMonth() + 1) : undefined,
    quarter: period === 'QUARTER' ? (search.quarter ?? quarterOf(now)) : undefined,
    propertyId: search.propertyId,
    tab: search.tab ?? 'registru',
    q: (search.q ?? '').trim(),
    category: search.category,
    method: search.method,
    rent: urlBoolean(search.rent) ?? true,
    expenses: urlBoolean(search.expenses) ?? true,
    memo: urlBoolean(search.memo) ?? true,
    groupByCategory: search.groupBy === 'category',
    measure: search.measure ?? 'net',
  };
}

/** How a control writes the URL: a functional update, pushed — or replaced while typing. */
export type LedgerSearchWriter = (
  next: (previous: LedgerSearch) => LedgerSearch,
  options?: { replace?: boolean },
) => void;

/** The part of the URL the ◀ ▶ arrows write. */
export type WindowStep = Pick<LedgerSearch, 'year' | 'month' | 'quarter'>;

/**
 * Where ◀ (`-1`) or ▶ (`+1`) would take the window, or `undefined` when it cannot go there —
 * which disables the arrow rather than hiding it (§4.7).
 *
 * The upper bound is „now": a month, a quarter or a year that has not started is a page of
 * zeros, and a YTD of one is refused outright (`reporting-period-in-future`). The lower bound
 * is the schema's own calendar.
 */
export function stepWindow(
  filters: LedgerFilters,
  delta: 1 | -1,
  now: Date,
): WindowStep | undefined {
  const thisYear = now.getFullYear();

  switch (filters.period) {
    case 'MONTH': {
      const current: Period = { year: filters.year, month: filters.month ?? now.getMonth() + 1 };
      const next = shiftPeriod(current, delta);
      const unchanged = next.year === current.year && next.month === current.month;
      const future =
        next.year > thisYear || (next.year === thisYear && next.month > now.getMonth() + 1);
      if (unchanged || future || next.year < MIN_REPORT_YEAR) return undefined;
      return { year: next.year, month: next.month };
    }
    case 'QUARTER': {
      const quarter = filters.quarter ?? quarterOf(now);
      const zeroBased = filters.year * 4 + (quarter - 1) + delta;
      const year = Math.floor(zeroBased / 4);
      const nextQuarter = (zeroBased % 4) + 1;
      const future = year > thisYear || (year === thisYear && nextQuarter > quarterOf(now));
      if (future || year < MIN_REPORT_YEAR || year > MAX_REPORT_YEAR) return undefined;
      return { year, quarter: nextQuarter };
    }
    default: {
      const year = filters.year + delta;
      if (year > thisYear || year < MIN_REPORT_YEAR) return undefined;
      return { year };
    }
  }
}

/** The four quarter names, as literal `t()` calls so the i18n gate sees them. */
const QUARTER_LABELS: readonly ((t: TFunction) => string)[] = [
  (t) => t('ledger.quarters.q1'),
  (t) => t('ledger.quarters.q2'),
  (t) => t('ledger.quarters.q3'),
  (t) => t('ledger.quarters.q4'),
];

/** „mai 2026", „Trimestrul II 2026", „Anul 2026", „2026, de la începutul anului". */
export function windowHeading(filters: LedgerFilters, now: Date, t: TFunction): string {
  switch (filters.period) {
    case 'MONTH':
      return formatPeriod({ year: filters.year, month: filters.month ?? now.getMonth() + 1 }, t);
    case 'QUARTER': {
      const label = QUARTER_LABELS[(filters.quarter ?? quarterOf(now)) - 1];
      return `${label === undefined ? '' : label(t)} ${filters.year}`.trim();
    }
    case 'YEAR':
      return t('ledger.window.year', { year: filters.year });
    default:
      return t('ledger.window.ytd', { year: filters.year });
  }
}

/** „01.01.2026 – 30.09.2026" — the window as the server resolved it, under the heading. */
export function windowRange(period: EconomicPnlPeriod): string {
  return `${formatDate(period.from)} – ${formatDate(period.to)}`;
}

/**
 * Whether any ROW-LEVEL filter is on — the condition under which the table's subtotals are
 * recomputed from the rows on screen rather than read off the server (D-2149). The window and
 * the apartment are not row-level: the server already narrowed to them.
 */
export function isRowFilterActive(filters: LedgerFilters): boolean {
  return (
    filters.q !== '' ||
    filters.category !== undefined ||
    filters.method !== undefined ||
    !filters.rent ||
    !filters.expenses ||
    !filters.memo
  );
}

/**
 * „Șterge filtrele": every row-level param and the grouping back to their absence, in one URL
 * write. The window, the apartment, the tab and the measure stay — they are the view, not a
 * filter over it.
 */
export function clearRowFilters(previous: LedgerSearch): LedgerSearch {
  return {
    ...previous,
    rent: undefined,
    expenses: undefined,
    memo: undefined,
    category: undefined,
    method: undefined,
    q: undefined,
    groupBy: undefined,
  };
}

/** Which of the three toggles a row kind answers to. */
export function hasKind(filters: LedgerFilters, kind: CashFlowKind): boolean {
  switch (kind) {
    case 'RENT_COLLECTED':
    case 'UNALLOCATED_CREDIT':
      return filters.rent;
    case 'EXPENSE_PAID':
      return filters.expenses;
    default:
      return filters.memo;
  }
}
